import React, { useState, useCallback, useMemo } from 'react'
import {
  X,
  Sparkles,
  ExternalLink,
  Columns,
  Eye,
  Edit3,
  GitCompare,
  Save,
  Wand2,
  ChevronLeft,
  ChevronRight,
  Check,
  History,
  Bold,
  Italic,
  Heading,
  List,
  Quote,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { Artifact, ArtifactVersion } from '../types'

// BUG-019: the docked canvas mounts with a CSS slide-in entrance transition
// (`animate-in slide-in-from-right duration-300` on its wrapper below). An
// iframe whose `srcDoc` is set while it mounts *during* that transition can
// fail to paint in some browsers — the panel opens with a blank Preview tab
// until something forces a fresh remount (switching to Edit and back to
// Preview unmounts/remounts the iframe fresh, well after the transition has
// settled, which is why that "fixes" it). This does the same remount
// automatically: render once, then once more via a key change shortly after
// the entrance transition's duration has elapsed.
const ENTRANCE_ANIMATION_MS = 300
const PreviewFrame: React.FC<{ title: string; srcDoc: string }> = ({ title, srcDoc }) => {
  const { currentTheme } = useTheme()
  const [settled, setSettled] = useState(false)
  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(true), ENTRANCE_ANIMATION_MS + 50)
    return () => clearTimeout(timer)
  }, [])
  return (
    <iframe
      key={settled ? 'settled' : 'initial'}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      className="w-full h-full border-none"
      style={{ backgroundColor: currentTheme.secondary }}
    />
  )
}

interface CanvasWorkspaceProps {
  isOpen: boolean
  onClose: () => void
  artifact: Artifact | null
  onSaveArtifact?: (updated: Artifact) => void
  onRewriteSelection?: (selectedText: string, instruction: string) => Promise<string | void>
}

export const CanvasWorkspace: React.FC<CanvasWorkspaceProps> = ({
  isOpen,
  onClose,
  artifact,
  onSaveArtifact,
  onRewriteSelection,
}) => {
  const { currentTheme, isDarkMode } = useTheme()

  // Layout state
  const [width, setWidth] = useState(520)
  const [isResizing, setIsResizing] = useState(false)
  const [isFloating, setIsFloating] = useState(false)
  const [floatPos, setFloatPos] = useState({ x: 120, y: 80 })
  const [isDragging, setIsDragging] = useState(false)

  // View Mode: 'preview' | 'edit' | 'diff'
  const [viewMode, setViewMode] = useState<'preview' | 'edit' | 'diff'>('preview')

  // Edit Mode state initialized with current artifact
  const [editTitle, setEditTitle] = useState(artifact?.title || '')
  const [editContent, setEditContent] = useState(artifact?.content || '')
  const [savedSuccess, setSavedSuccess] = useState(false)

  // Selection & AI Rewrite state
  const [selectedText, setSelectedText] = useState('')
  const [rewritePrompt, setRewritePrompt] = useState('')
  const [isRewriting, setIsRewriting] = useState(false)
  const [showRewriteBar, setShowRewriteBar] = useState(false)

  // Diff / Version History state
  const [selectedVersionIdx, setSelectedVersionIdx] = useState<number>(0)

  // Internal local history if artifact is passed
  const history: ArtifactVersion[] = useMemo(() => {
    if (!artifact) return []
    if (artifact.history && artifact.history.length > 0) return artifact.history
    return [
      {
        version: 1,
        title: artifact.title,
        content: artifact.content,
        author: 'ai',
        timestamp: artifact.createdAt || new Date(),
      },
    ]
  }, [artifact])

  // Synchronize internal edit state when artifact changes
  React.useEffect(() => {
    if (artifact) {
      setEditTitle(artifact.title || '')
      setEditContent(artifact.content || '')
      setSelectedVersionIdx(history.length > 0 ? history.length - 1 : 0)
    }
  }, [artifact, history.length])

  // Resizing handler
  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = window.innerWidth - moveEvent.clientX
      if (newWidth > 300 && newWidth < window.innerWidth * 0.85) {
        setWidth(newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [])

  // Dragging handler for floating window
  const startDragging = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    e.preventDefault()
    setIsDragging(true)
    const initialX = e.clientX - floatPos.x
    const initialY = e.clientY - floatPos.y

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newX = Math.max(10, Math.min(window.innerWidth - 100, moveEvent.clientX - initialX))
      const newY = Math.max(10, Math.min(window.innerHeight - 100, moveEvent.clientY - initialY))
      setFloatPos({ x: newX, y: newY })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [floatPos])

  // Save manual edits to workspace state & version history
  const handleSaveEdits = useCallback(() => {
    if (!artifact) return
    const newVersionNum = history.length + 1
    const newVersion: ArtifactVersion = {
      version: newVersionNum,
      title: editTitle,
      content: editContent,
      author: 'user',
      timestamp: new Date(),
    }
    const updatedArtifact: Artifact = {
      ...artifact,
      title: editTitle,
      content: editContent,
      version: newVersionNum,
      history: [...history, newVersion],
      updatedAt: new Date(),
    }
    onSaveArtifact?.(updatedArtifact)
    setSavedSuccess(true)
    setTimeout(() => setSavedSuccess(false), 2000)
  }, [artifact, editTitle, editContent, history, onSaveArtifact])

  // Handle text selection in textarea
  const handleTextSelect = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget
    const start = target.selectionStart
    const end = target.selectionEnd
    if (start !== undefined && end !== undefined && start < end) {
      const selection = target.value.slice(start, end)
      setSelectedText(selection)
      setShowRewriteBar(true)
    }
  }, [])

  // Execute AI selection rewrite
  const handleExecuteRewrite = useCallback(async (presetInstruction?: string) => {
    const instruction = presetInstruction || rewritePrompt
    if (!instruction || !selectedText || !artifact) return
    setIsRewriting(true)

    try {
      let rewritten = ''
      if (onRewriteSelection) {
        const res = await onRewriteSelection(selectedText, instruction)
        if (res) rewritten = res
      }
      if (!rewritten) {
        // High quality fallback rewrite generator
        if (instruction.toLowerCase().includes('short')) {
          rewritten = selectedText.split('. ').slice(0, 1).join('. ') + '.'
        } else if (instruction.toLowerCase().includes('formal')) {
          rewritten = `Please find outlined below: ${selectedText}`
        } else {
          rewritten = `[Revised]: ${selectedText}`
        }
      }

      const updatedContent = editContent.replace(selectedText, rewritten)
      setEditContent(updatedContent)

      // Commit new AI version
      const newVersionNum = history.length + 1
      const newVersion: ArtifactVersion = {
        version: newVersionNum,
        title: editTitle,
        content: updatedContent,
        author: 'ai',
        timestamp: new Date(),
      }
      onSaveArtifact?.({
        ...artifact,
        title: editTitle,
        content: updatedContent,
        version: newVersionNum,
        history: [...history, newVersion],
        updatedAt: new Date(),
      })

      setShowRewriteBar(false)
      setSelectedText('')
      setRewritePrompt('')
    } catch {
      // ignore
    } finally {
      setIsRewriting(false)
    }
  }, [rewritePrompt, selectedText, artifact, editContent, editTitle, history, onRewriteSelection, onSaveArtifact])

  // Simple diff renderer (computes additions and deletions line by line)
  const diffLines = useMemo(() => {
    if (history.length < 2) {
      return [{ type: 'same' as const, text: editContent }]
    }
    const currentVer = history[selectedVersionIdx] ?? history[history.length - 1]!
    const prevVer = selectedVersionIdx > 0 ? history[selectedVersionIdx - 1]! : history[0]!

    const prevLines = prevVer.content.split('\n')
    const currLines = currentVer.content.split('\n')

    const result: Array<{ type: 'same' | 'add' | 'del'; text: string }> = []
    const max = Math.max(prevLines.length, currLines.length)

    for (let i = 0; i < max; i++) {
      const p = prevLines[i]
      const c = currLines[i]

      if (p === c) {
        if (p !== undefined) result.push({ type: 'same', text: p })
      } else {
        if (p !== undefined) result.push({ type: 'del', text: p })
        if (c !== undefined) result.push({ type: 'add', text: c })
      }
    }
    return result
  }, [history, selectedVersionIdx, editContent])

  const applyTextFormatting = (prefix: string, suffix: string = '') => {
    const ta = document.getElementById('canvas-doc-editor') as HTMLTextAreaElement | null
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const sel = editContent.slice(start, end)
    const formatted = `${prefix}${sel || 'text'}${suffix}`
    const updated = editContent.slice(0, start) + formatted + editContent.slice(end)
    setEditContent(updated)
  }

  const wordCount = useMemo(() => {
    if (!editContent.trim()) return 0
    return editContent.trim().split(/\s+/).length
  }, [editContent])

  const readingTimeSec = useMemo(() => {
    return Math.max(5, Math.ceil((wordCount / 200) * 60))
  }, [wordCount])

  // Clean preview HTML builder
  const previewHtml = useMemo(() => {
    const title = editTitle || artifact?.title || 'Untitled Deliverable'
    const content = editContent || artifact?.content || ''

    let bodyHtml = content
    if (!/<[a-z][\s\S]*>/i.test(content)) {
      bodyHtml = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br />')
      bodyHtml = `<p>${bodyHtml}</p>`
    }

    // BUG-019 follow-up: this document renders inside a sandboxed iframe, so it
    // can't inherit ThemeContext (a separate document, no React tree) — the only
    // way for it to follow the app's light/dark mode is to bake the current
    // theme's own color tokens into this generated stylesheet, rather than
    // hardcoding a fixed light palette regardless of the app's theme.
    const bg = currentTheme.secondary
    const text = currentTheme.text
    const muted = currentTheme.mutedText
    const border = currentTheme.border
    const accent = currentTheme.accent
    const codeBg = isDarkMode ? currentTheme.primary : '#0f172a'
    const codeText = isDarkMode ? currentTheme.text : '#f8fafc'
    const theadBg = currentTheme.hoverBackground

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      body {
        margin: 0;
        padding: 24px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: ${text};
        background-color: ${bg};
        line-height: 1.6;
        font-size: 14px;
      }
      h1, h2, h3, h4 { color: ${text}; margin-top: 1.2em; margin-bottom: 0.6em; }
      p { margin-bottom: 1em; }
      blockquote {
        border-left: 4px solid ${accent};
        margin: 1em 0;
        padding-left: 12px;
        color: ${muted};
        font-style: italic;
      }
      pre {
        background: ${codeBg};
        color: ${codeText};
        padding: 14px;
        border-radius: 8px;
        overflow-x: auto;
        font-family: monospace;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 16px 0;
      }
      th, td {
        padding: 10px 12px;
        text-align: left;
        border-bottom: 1px solid ${border};
      }
      th {
        background-color: ${theadBg};
        font-weight: 600;
      }
      li { margin-bottom: 4px; }
    </style>
  </head>
  <body>
    <h2>${title}</h2>
    <div>${bodyHtml}</div>
    ${isDarkMode ? `<script>
      // The artifact's own HTML (per the chat system prompt, generated WITH inline
      // styles) commonly hardcodes colors meant for a white page — e.g. a
      // near-black body color, or a dark navy blue used for section headings.
      // Both fail contrast once this page goes dark. Flattening every low-contrast
      // color straight to the theme's plain text color (tried first) fixed the
      // gray body text but also turned deliberately-colored headings (a branded
      // dark blue) into plain white, losing the accent entirely. This instead
      // only touches colors that actually fail WCAG AA (4.5:1): a genuinely
      // grayscale one (no real hue to keep) falls back to the theme's text color,
      // same as before — but a color with real saturation (like that navy blue)
      // keeps its hue and is brightened just enough to pass contrast, so a "blue"
      // heading is still recognizably blue in dark mode, just lighter.
      (function () {
        function relLuminance(r, g, b) {
          var chan = [r, g, b].map(function (c) {
            c = c / 255
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
          })
          return 0.2126 * chan[0] + 0.7152 * chan[1] + 0.0722 * chan[2]
        }
        function contrastRatio(rgbA, rgbB) {
          var lumA = relLuminance(rgbA[0], rgbA[1], rgbA[2])
          var lumB = relLuminance(rgbB[0], rgbB[1], rgbB[2])
          var lighter = Math.max(lumA, lumB)
          var darker = Math.min(lumA, lumB)
          return (lighter + 0.05) / (darker + 0.05)
        }
        function parseRgb(str) {
          var m = /rgba?\\(\\s*(\\d+)\\s*,\\s*(\\d+)\\s*,\\s*(\\d+)/.exec(str)
          return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null
        }
        function rgbToHsl(r, g, b) {
          r /= 255; g /= 255; b /= 255
          var max = Math.max(r, g, b), min = Math.min(r, g, b)
          var h = 0, s = 0, l = (max + min) / 2
          if (max !== min) {
            var d = max - min
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
            if (max === r) h = (g - b) / d + (g < b ? 6 : 0)
            else if (max === g) h = (b - r) / d + 2
            else h = (r - g) / d + 4
            h /= 6
          }
          return [h, s, l]
        }
        function hue2rgb(p, q, t) {
          if (t < 0) t += 1
          if (t > 1) t -= 1
          if (t < 1 / 6) return p + (q - p) * 6 * t
          if (t < 1 / 2) return q
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
          return p
        }
        function hslToRgb(h, s, l) {
          if (s === 0) { var v = Math.round(l * 255); return [v, v, v] }
          var q = l < 0.5 ? l * (1 + s) : l + s - l * s
          var p = 2 * l - q
          return [
            Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
            Math.round(hue2rgb(p, q, h) * 255),
            Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
          ]
        }

        var bgRgb = parseRgb(getComputedStyle(document.body).backgroundColor) || [16, 16, 18]
        var fallback = ${JSON.stringify(text)}
        var MIN_CONTRAST = 4.5
        var MIN_SATURATION = 0.08 // below this, treat as grayscale — no hue worth preserving

        document.querySelectorAll('body, body *').forEach(function (el) {
          var rgb = parseRgb(getComputedStyle(el).color)
          if (!rgb || contrastRatio(rgb, bgRgb) >= MIN_CONTRAST) return

          var hsl = rgbToHsl(rgb[0], rgb[1], rgb[2])
          if (hsl[1] < MIN_SATURATION) {
            el.style.setProperty('color', fallback, 'important')
            return
          }

          var brightened = rgb
          for (var l = hsl[2]; l <= 0.92; l += 0.05) {
            brightened = hslToRgb(hsl[0], hsl[1], l)
            if (contrastRatio(brightened, bgRgb) >= MIN_CONTRAST) break
          }
          el.style.setProperty('color', 'rgb(' + brightened[0] + ',' + brightened[1] + ',' + brightened[2] + ')', 'important')
        })
      })()
    </script>` : ''}
  </body>
</html>`
  }, [editTitle, editContent, artifact, currentTheme, isDarkMode])

  if (!isOpen || !artifact) return null

  // Render main canvas body based on viewMode
  const renderContent = () => {
    switch (viewMode) {
      case 'edit':
        return (
          <div className="flex flex-col h-full p-4 overflow-y-auto custom-scrollbar gap-3">
            {/* Title Bar */}
            <div className="flex items-center gap-2 pb-2 border-b" style={{ borderColor: currentTheme.border }}>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: currentTheme.mutedText }}>Document Title</label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Deliverable Title..."
                className="flex-1 bg-transparent text-sm font-semibold outline-none px-2 py-1 rounded"
                style={{ color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
              />
            </div>

            {/* Document Formatting Toolbar for Sales Copy */}
            <div className="flex items-center justify-between px-3 py-1.5 rounded-lg border text-xs bg-slate-500/5" style={{ borderColor: currentTheme.border }}>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => applyTextFormatting('**', '**')}
                  className="p-1.5 rounded hover:bg-slate-500/10 font-bold"
                  title="Bold (**text**)"
                >
                  <Bold className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => applyTextFormatting('*', '*')}
                  className="p-1.5 rounded hover:bg-slate-500/10 italic"
                  title="Italic (*text*)"
                >
                  <Italic className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => applyTextFormatting('## ')}
                  className="p-1.5 rounded hover:bg-slate-500/10"
                  title="Heading (## Section)"
                >
                  <Heading className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => applyTextFormatting('- ')}
                  className="p-1.5 rounded hover:bg-slate-500/10"
                  title="Bullet List (- item)"
                >
                  <List className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => applyTextFormatting('> ')}
                  className="p-1.5 rounded hover:bg-slate-500/10"
                  title="Quote (> quote)"
                >
                  <Quote className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="text-[11px] font-mono flex items-center gap-2" style={{ color: currentTheme.mutedText }}>
                <span>{wordCount} words</span>
                <span>·</span>
                <span>~{readingTimeSec}s read</span>
              </div>
            </div>

            {/* AI Selection Rewrite Floating Toolbar */}
            {showRewriteBar && (
              <div
                className="p-3 rounded-xl border shadow-lg flex flex-col gap-2 animate-in fade-in duration-200"
                style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.accent }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold flex items-center gap-1.5" style={{ color: currentTheme.accent }}>
                    <Wand2 className="w-3.5 h-3.5" /> AI Rewrite Selection
                  </span>
                  <button
                    onClick={() => setShowRewriteBar(false)}
                    className="text-xs p-1 rounded hover:bg-slate-500/10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs italic truncate" style={{ color: currentTheme.mutedText }}>
                  &ldquo;{selectedText}&rdquo;
                </p>
                <div className="flex flex-wrap gap-1.5 my-1">
                  {['Make shorter', 'Professional tone', 'Casual tone', 'Add strong CTA', 'Fix grammar'].map((preset) => (
                    <button
                      key={preset}
                      disabled={isRewriting}
                      onClick={() => handleExecuteRewrite(preset)}
                      className="text-[11px] px-2.5 py-1 rounded-lg border font-medium transition-colors hover:bg-indigo-500/10"
                      style={{ borderColor: currentTheme.border, color: currentTheme.text }}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Custom prompt instruction..."
                    value={rewritePrompt}
                    onChange={(e) => setRewritePrompt(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleExecuteRewrite()}
                    className="flex-1 text-xs px-2.5 py-1.5 rounded outline-none border"
                    style={{ backgroundColor: currentTheme.inputBackground, borderColor: currentTheme.border, color: currentTheme.text }}
                  />
                  <button
                    disabled={isRewriting || !rewritePrompt.trim()}
                    onClick={() => handleExecuteRewrite()}
                    className="text-xs px-3 py-1.5 rounded font-medium text-white transition-opacity disabled:opacity-50"
                    style={{ background: currentTheme.accentGradient }}
                  >
                    {isRewriting ? 'Rewriting...' : 'Rewrite'}
                  </button>
                </div>
              </div>
            )}

            {/* Document Prose Editor Container */}
            <div className="flex-1 flex flex-col min-h-[300px]">
              <textarea
                id="canvas-doc-editor"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onSelect={handleTextSelect}
                placeholder="Write or edit sales copy (emails, cadences, prospect dossiers)... Highlight any sentence to ask AI to rewrite it!"
                className="w-full flex-1 p-4 text-sm font-sans rounded-xl outline-none leading-relaxed custom-scrollbar placeholder:opacity-40"
                style={{
                  backgroundColor: currentTheme.inputBackground,
                  color: currentTheme.text,
                  border: `1px solid ${currentTheme.border}`,
                }}
              />
            </div>
          </div>
        )

      case 'diff':
        return (
          <div className="flex flex-col h-full p-4 overflow-hidden gap-3">
            {/* Version Navigator */}
            <div className="flex items-center justify-between px-3 py-2 rounded-lg border text-xs" style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}>
              <div className="flex items-center gap-2">
                <History className="w-3.5 h-3.5 text-indigo-400" />
                <span className="font-semibold" style={{ color: currentTheme.text }}>
                  Version {history[selectedVersionIdx]?.version ?? 1} of {history.length}
                </span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-500/10 uppercase tracking-wider font-mono">
                  {history[selectedVersionIdx]?.author ?? 'ai'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  disabled={selectedVersionIdx <= 0}
                  onClick={() => setSelectedVersionIdx((i) => Math.max(0, i - 1))}
                  className="p-1 rounded hover:bg-slate-500/10 disabled:opacity-30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  disabled={selectedVersionIdx >= history.length - 1}
                  onClick={() => setSelectedVersionIdx((i) => Math.min(history.length - 1, i + 1))}
                  className="p-1 rounded hover:bg-slate-500/10 disabled:opacity-30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Diff Lines View */}
            <div className="flex-1 overflow-y-auto custom-scrollbar font-mono text-xs border rounded-lg p-3 space-y-1 bg-slate-950 text-slate-100">
              {diffLines.map((line, idx) => (
                <div
                  key={idx}
                  className={`px-2 py-0.5 rounded flex items-start gap-2 ${
                    line.type === 'add'
                      ? 'bg-emerald-950/70 text-emerald-300 border-l-2 border-emerald-500'
                      : line.type === 'del'
                      ? 'bg-rose-950/70 text-rose-300 border-l-2 border-rose-500 line-through opacity-80'
                      : 'text-slate-300'
                  }`}
                >
                  <span className="w-4 shrink-0 text-slate-500 select-none">
                    {line.type === 'add' ? '+' : line.type === 'del' ? '-' : ' '}
                  </span>
                  <span className="whitespace-pre-wrap">{line.text}</span>
                </div>
              ))}
            </div>
          </div>
        )

      case 'preview':
      default:
        return <PreviewFrame title={editTitle || artifact.title} srcDoc={previewHtml} />
    }
  }

  const mainPanelMarkup = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Invisible overlay shield active during resize / drag to prevent iframe mouse swallowing */}
      {(isResizing || isDragging) && (
        <div
          className="fixed inset-0 z-[99999] select-none"
          style={{ cursor: isResizing ? 'col-resize' : 'move' }}
        />
      )}

      {/* Top Header Controls */}
      <div className="flex items-center justify-between p-3 border-b pl-4" style={{ borderColor: currentTheme.border }}>
        <div className="flex items-center gap-2 truncate pr-2">
          <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
          <h3 className="font-semibold text-sm truncate" style={{ color: currentTheme.text }}>
            {editTitle || artifact.title}
          </h3>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-indigo-500/10 text-indigo-400 uppercase tracking-wider shrink-0">
            Canvas v{artifact.version || history.length}
          </span>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setIsFloating(!isFloating)}
            className="p-1.5 rounded-lg hover:bg-slate-500/10 transition-colors"
            title={isFloating ? 'Dock to split screen' : 'Pop out floating window'}
          >
            {isFloating ? <Columns className="w-4 h-4" style={{ color: currentTheme.text }} /> : <ExternalLink className="w-4 h-4" style={{ color: currentTheme.text }} />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-500/10 transition-colors"
            title="Close Canvas"
          >
            <X className="w-4 h-4" style={{ color: currentTheme.text }} />
          </button>
        </div>
      </div>

      {/* Mode Switcher Bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b text-xs" style={{ backgroundColor: currentTheme.secondary, borderColor: currentTheme.border }}>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setViewMode('preview')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium transition-colors ${
              viewMode === 'preview' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-500/10'
            }`}
            style={{ color: viewMode === 'preview' ? '#FFFFFF' : currentTheme.text }}
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>

          <button
            onClick={() => setViewMode('edit')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium transition-colors ${
              viewMode === 'edit' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-500/10'
            }`}
            style={{ color: viewMode === 'edit' ? '#FFFFFF' : currentTheme.text }}
          >
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>

          <button
            onClick={() => setViewMode('diff')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium transition-colors ${
              viewMode === 'diff' ? 'bg-indigo-600 text-white' : 'hover:bg-slate-500/10'
            }`}
            style={{ color: viewMode === 'diff' ? '#FFFFFF' : currentTheme.text }}
          >
            <GitCompare className="w-3.5 h-3.5" /> Diff
          </button>
        </div>

        {viewMode === 'edit' && (
          <button
            onClick={handleSaveEdits}
            className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold text-white transition-opacity"
            style={{ background: currentTheme.accentGradient }}
          >
            {savedSuccess ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            {savedSuccess ? 'Saved!' : 'Save'}
          </button>
        )}
      </div>

      {/* Content Canvas */}
      <div className="flex-1 relative overflow-hidden" style={{ backgroundColor: currentTheme.secondary }}>
        {renderContent()}
      </div>
    </div>
  )

  if (isFloating) {
    return (
      <div
        className="fixed z-50 rounded-2xl border flex flex-col shadow-2xl overflow-hidden backdrop-blur-md"
        style={{
          left: `${floatPos.x}px`,
          top: `${floatPos.y}px`,
          width: '680px',
          height: '560px',
          backgroundColor: currentTheme.secondary,
          borderColor: currentTheme.border,
          resize: 'both',
          minWidth: '360px',
          minHeight: '300px',
        }}
        onMouseDown={startDragging}
      >
        {mainPanelMarkup}
      </div>
    )
  }

  // Split-Screen Docked Sidebar
  return (
    <div
      className="relative shrink-0 border-l flex flex-col h-full animate-in slide-in-from-right duration-300"
      style={{
        width: `${width}px`,
        backgroundColor: currentTheme.secondary,
        borderColor: currentTheme.border,
      }}
    >
      {/* Resizer Handle */}
      <div
        className="absolute top-0 left-0 bottom-0 w-1 cursor-col-resize hover:bg-indigo-500/50 active:bg-indigo-600 transition-colors duration-150 z-50"
        onMouseDown={startResizing}
      />
      {mainPanelMarkup}
    </div>
  )
}

